import React, { useEffect } from "react";
import { JobSearchResults } from "@themuse/design-system";
import getConfig from "next/config";

import { trackSnowplowImpressions } from "@themuse/snowplow-js-client";
import { useAppState, useAppDispatch } from "hooks/AppContext";
import { SingleJob } from "interfaces/api/apiInterfaces";
import { isMobile } from "utils/browser";
import { getJobTrackingProps } from "utils/tracking";
import { SimilarJobsActionTypes } from "interfaces/context/appContextInterfaces";
import { JobApplyButtonSideBarModule } from "./JobApplyButtonSideBarModule";
import { SimilarJobsSidebarModule } from "./SimilarJobsSidebarModule";
import { SuggestedSearchesSidebarModule } from "./SuggestedSearchesSidebarModule";
import { CompanyInformationModule } from "./CompanyInformationModule";
import styles from "./JobIndividualSidebar.module.scss";
import { AdditionalJobLinkList } from "./AdditionalJobLinkList";

interface JobIndividualSidebarProps {
    getSimilarJobs: (
        job: SingleJob,
        callback: (similarJobsSearchResult: JobSearchResults) => void
    ) => void;
    setIsOpen?: (x: boolean) => void;
    isDirectApply: boolean;
}

export const JobIndividualSidebar = (props: JobIndividualSidebarProps) => {
    const { getSimilarJobs, setIsOpen, isDirectApply } = props;
    const [buttonPlacement, setButtonPlacement] = React.useState("right_rail");
    const { job, company, similarJobs } = useAppState();
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const appDispatch = useAppDispatch();
    const jobTrackingProps = getJobTrackingProps(job, company);
    const trackJobApplyProps = {
        ...jobTrackingProps,
        placement_position: buttonPlacement
    };

    const setSimilarJobsCallback = (similarJobsSearchResult: JobSearchResults) => {
        appDispatch({
            type: SimilarJobsActionTypes.updateSimilarJobs,
            payload: similarJobsSearchResult
        });
    };

    useEffect(() => {
        if (!similarJobs?.hits?.length) {
            getSimilarJobs(job, setSimilarJobsCallback);
        }
    }, []);

    useEffect(() => {
        if (isMobile()) {
            setButtonPlacement("mobile_fixed_bottom");
        }
    }, []);

    useEffect(() => {
        if (similarJobs) {
            trackSnowplowImpressions();
        }
    }, [similarJobs?.hits]);

    return (
        <>
            <CompanyInformationModule company={company} job={job} displayOnMobile={true} />
            <aside className={styles.jobSidebarContainer}>
                <JobApplyButtonSideBarModule
                    jobState={job?.state}
                    jobId={job?.id}
                    trackJobApplyProps={trackJobApplyProps}
                    setIsOpen={setIsOpen}
                    isDirectApply={isDirectApply}
                />
                <div className={styles.sidebarModules}>
                    <SimilarJobsSidebarModule similarJobs={similarJobs} jobId={job.id} />
                    <SuggestedSearchesSidebarModule
                        job={job}
                        company={company}
                        museWebHost={museWebHost}
                        trackingProps={jobTrackingProps}
                    />
                </div>
            </aside>
            <AdditionalJobLinkList />
            <JobApplyButtonSideBarModule
                jobState={job?.state}
                jobId={job?.id}
                trackJobApplyProps={trackJobApplyProps}
                mobile={true}
                setIsOpen={setIsOpen}
                isDirectApply={isDirectApply}
            />
        </>
    );
};
